import { useCallback, useEffect, useRef, type MutableRefObject } from 'react';

type StickyHeaderOptions = {
  /**
   * When true we skip sticky behavior entirely. Useful while loading states
   * render alternative content (e.g. skeletons) inside the grid container.
   */
  disabled?: boolean;
};

type StickyHeaderApi = {
  /**
   * Apply to the surrounding element that contains the DataGrid. The hook
   * needs this to compute positioning and attach event listeners.
   */
  wrapperRef: MutableRefObject<HTMLDivElement | null>;
};

const GLOBAL_HEADER_ID = 'header';

const useStickyGridHeader = ({
  disabled = false,
}: StickyHeaderOptions = {}): StickyHeaderApi => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const stickyOffsetRef = useRef<number>(0);

  const refreshOffset = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const anchor = document.getElementById(GLOBAL_HEADER_ID);
    const computedOffset = anchor
      ? Math.round(anchor.getBoundingClientRect().bottom)
      : 0;

    stickyOffsetRef.current = computedOffset;
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || disabled) {
      return;
    }

    refreshOffset();

    const handleResize = () => refreshOffset();
    window.addEventListener('resize', handleResize);

    const anchor = document.getElementById(GLOBAL_HEADER_ID);
    let observer: ResizeObserver | undefined;

    if (anchor && 'ResizeObserver' in window) {
      observer = new ResizeObserver(() => refreshOffset());
      observer.observe(anchor);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      observer?.disconnect();
    };
  }, [disabled, refreshOffset]);

  useEffect(() => {
    if (disabled) {
      return;
    }

    const container = wrapperRef.current;
    if (!container) {
      return;
    }

    let disposed = false;
    let cleanup: (() => void) | null = null;
    let rafId: number | null = null;

    const init = () => {
      if (disposed) {
        return;
      }

      const topContainer = container.querySelector<HTMLElement>(
        '.MuiDataGrid-topContainer'
      );
      const columnHeaders = container.querySelector<HTMLElement>(
        '.MuiDataGrid-columnHeaders'
      );
      const virtualScroller = container.querySelector<HTMLElement>(
        '.MuiDataGrid-virtualScroller'
      );

      if (!topContainer || !columnHeaders) {
        rafId = window.requestAnimationFrame(init);
        return;
      }

      const placeholder = document.createElement('div');
      placeholder.style.width = '100%';
      placeholder.style.display = 'none';
      placeholder.style.pointerEvents = 'none';
      topContainer.after(placeholder);

      let isFixed = false;

      const syncDimensions = () => {
        if (!isFixed) {
          return;
        }

        const wrapperRect = container.getBoundingClientRect();
        topContainer.style.left = `${wrapperRect.left}px`;
        topContainer.style.width = `${wrapperRect.width}px`;
      };

      const applyFixed = () => {
        const wrapperRect = container.getBoundingClientRect();
        const headerRect = topContainer.getBoundingClientRect();

        placeholder.style.height = `${headerRect.height}px`;
        placeholder.style.display = 'block';

        topContainer.style.position = 'fixed';
        topContainer.style.top = `${stickyOffsetRef.current}px`;
        topContainer.style.left = `${wrapperRect.left}px`;
        topContainer.style.width = `${wrapperRect.width}px`;
        topContainer.style.zIndex = '20';
        topContainer.style.boxShadow = '0 2px 6px rgba(25, 42, 62, 0.1)';

        isFixed = true;
      };

      const releaseFixed = () => {
        topContainer.style.position = '';
        topContainer.style.top = '';
        topContainer.style.left = '';
        topContainer.style.width = '';
        topContainer.style.zIndex = '';
        topContainer.style.boxShadow = '';

        placeholder.style.display = 'none';
        isFixed = false;
      };

      const syncHorizontalScroll = () => {
        if (!virtualScroller || !columnHeaders) {
          return;
        }

        columnHeaders.scrollLeft = virtualScroller.scrollLeft;
      };

      const updatePosition = () => {
        const wrapperRect = container.getBoundingClientRect();
        const headerHeight = topContainer.getBoundingClientRect().height;
        const { current: offset } = stickyOffsetRef;

        const shouldFix =
          wrapperRect.top <= offset &&
          wrapperRect.bottom > offset + headerHeight;

        if (shouldFix) {
          if (!isFixed) {
            applyFixed();
            syncHorizontalScroll();
          } else {
            placeholder.style.height = `${headerHeight}px`;
            syncDimensions();
          }
        } else if (isFixed) {
          releaseFixed();
        }
      };

      const handleWindowScroll = () => {
        updatePosition();
        if (isFixed) {
          syncHorizontalScroll();
        }
      };

      const handleResize = () => updatePosition();

      const handleVirtualScroll = () => {
        if (isFixed) {
          syncHorizontalScroll();
        }
      };

      window.addEventListener('scroll', handleWindowScroll, { passive: true });
      window.addEventListener('resize', handleResize);
      virtualScroller?.addEventListener('scroll', handleVirtualScroll, {
        passive: true,
      });

      const resizeObserver =
        typeof ResizeObserver !== 'undefined'
          ? new ResizeObserver(() => updatePosition())
          : undefined;

      resizeObserver?.observe(container);
      resizeObserver?.observe(topContainer);

      updatePosition();

      cleanup = () => {
        window.removeEventListener('scroll', handleWindowScroll);
        window.removeEventListener('resize', handleResize);
        virtualScroller?.removeEventListener('scroll', handleVirtualScroll);
        resizeObserver?.disconnect();
        releaseFixed();
        placeholder.remove();
      };
    };

    init();

    return () => {
      disposed = true;
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      cleanup?.();
    };
  }, [disabled, refreshOffset]);

  return { wrapperRef };
};

export default useStickyGridHeader;
